import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Tag, DollarSign, Clock, Package, User, Link2, ToggleLeft, ToggleRight } from 'lucide-react';
import { toast } from 'sonner';
import { theme } from '../theme';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CreatorListingDetailPage() {
  const { listingId } = useParams();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [attachCount, setAttachCount] = useState(0);
  const [attachedPortfolios, setAttachedPortfolios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);

  const fetchListing = () => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/api/creator/utilities-marketplace/${listingId}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      setListing(res.data.listing);
      setAttachCount(res.data.attach_count || 0);
      setAttachedPortfolios(res.data.attached_portfolios || []);
    }).catch(() => toast.error('Listing not found'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchListing(); }, [listingId]);

  const toggleStatus = async () => {
    setToggling(true);
    const token = localStorage.getItem('token');
    const newStatus = listing.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
    try {
      await axios.put(`${API}/api/provider/utility-listings/${listingId}/status`, { status: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
      setListing(prev => ({ ...prev, status: newStatus }));
      toast.success(`Listing ${newStatus.toLowerCase()}`);
    } catch { toast.error('Failed to update status'); }
    setToggling(false);
  };

  if (loading) return <div style={{ padding: '60px', textAlign: 'center', color: theme.colors.text.secondary }}>Loading...</div>;
  if (!listing) return <div style={{ padding: '60px', textAlign: 'center', color: theme.colors.text.secondary }}>Not found</div>;

  const isOwner = true; // Navigated from My Listings, always owner context
  const statusColor = listing.status === 'ACTIVE' ? theme.colors.status.success : theme.colors.status.warning;

  return (
    <div data-testid="listing-detail-page" style={{ maxWidth: '900px', margin: '0 auto', padding: '32px 24px' }}>
      <button data-testid="listing-detail-back" onClick={() => navigate(-1)}
        style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', cursor: 'pointer', color: theme.colors.primary, fontSize: '14px', fontWeight: 600, marginBottom: '24px' }}>
        <ArrowLeft size={16} /> Back
      </button>

      {/* Header with status toggle */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <h1 data-testid="listing-detail-title" style={{ fontSize: '28px', fontWeight: 700, color: theme.colors.text.primary, margin: 0 }}>{listing.title}</h1>
            <span data-testid="listing-detail-status" style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 600, background: `${statusColor}18`, color: statusColor }}>{listing.status}</span>
          </div>
          <p style={{ fontSize: '15px', color: theme.colors.text.secondary, lineHeight: 1.6, maxWidth: '600px' }}>{listing.description}</p>
        </div>
        <Button data-testid="listing-toggle-status" onClick={toggleStatus} disabled={toggling} variant="outline"
          style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
          {listing.status === 'ACTIVE' ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
          {listing.status === 'ACTIVE' ? 'Pause' : 'Activate'}
        </Button>
      </div>

      {/* Info Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '32px' }}>
        {[
          { icon: <DollarSign size={14} color={theme.colors.status.success} />, label: 'Pricing', value: listing.pricing_model === 'FREE' ? 'Free' : `$${listing.price_amount || 0} ${listing.currency || 'USD'}` },
          { icon: <Tag size={14} color={theme.colors.primary} />, label: 'Fulfillment', value: listing.fulfillment_type || 'DIGITAL' },
          { icon: <Clock size={14} color={theme.colors.status.warning} />, label: 'Duration', value: listing.duration_days ? `${listing.duration_days} days` : 'Unlimited' },
          { icon: <Package size={14} color={theme.colors.primary} />, label: 'Max Redemptions', value: listing.max_redemptions || 'Unlimited' },
          { icon: <Link2 size={14} color={theme.colors.status.info} />, label: 'Attached Portfolios', value: attachCount },
          { icon: <User size={14} color={theme.colors.text.muted} />, label: 'Provider', value: listing.provider_name },
        ].map((item, i) => (
          <Card key={i}><CardContent className="pt-4 pb-4">
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
              {item.icon}<span style={{ fontSize: '11px', color: theme.colors.text.muted }}>{item.label}</span>
            </div>
            <p style={{ fontSize: '15px', fontWeight: 600, color: theme.colors.text.primary }}>{item.value}</p>
          </CardContent></Card>
        ))}
      </div>

      {/* Attached portfolios */}
      {attachedPortfolios.length > 0 && (
        <Card style={{ marginBottom: '20px' }}><CardContent className="pt-5 pb-5">
          <p style={{ fontSize: '13px', fontWeight: 600, color: theme.colors.text.primary, marginBottom: '12px' }}>Attached Portfolios</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {attachedPortfolios.map(p => (
              <div key={p.id} onClick={() => navigate(`/creator/portfolios/${p.id}`)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderRadius: '8px', background: theme.colors.bg.secondary, cursor: 'pointer' }}>
                <span style={{ fontSize: '14px', fontWeight: 500, color: theme.colors.text.primary }}>{p.name}</span>
                <span style={{ fontSize: '12px', color: theme.colors.text.muted }}>{p.status}</span>
              </div>
            ))}
          </div>
        </CardContent></Card>
      )}

      {/* Tags */}
      {listing.category_tags?.length > 0 && (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' }}>
          {listing.category_tags.map(tag => (
            <span key={tag} style={{ padding: '4px 12px', borderRadius: '16px', fontSize: '12px', background: `${theme.colors.primary}14`, color: theme.colors.primary, fontWeight: 500 }}>{tag}</span>
          ))}
        </div>
      )}

      {/* Redeem Instructions */}
      {listing.redeem_instructions && (
        <Card style={{ marginBottom: '20px' }}><CardContent className="pt-5 pb-5">
          <p style={{ fontSize: '13px', fontWeight: 600, color: theme.colors.text.primary, marginBottom: '8px' }}>Redeem Instructions</p>
          <p style={{ fontSize: '14px', color: theme.colors.text.secondary, lineHeight: 1.6 }}>{listing.redeem_instructions}</p>
        </CardContent></Card>
      )}

      <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: theme.colors.text.muted }}>
        <span>Created: {new Date(listing.created_at).toLocaleDateString()}</span>
        {listing.updated_at && <span>Updated: {new Date(listing.updated_at).toLocaleDateString()}</span>}
      </div>
    </div>
  );
}
